/**
 * Centralized Solana Configuration
 * @version 2025-02-06-v5
 *
 * All Solana connection settings should read from this module
 * to ensure consistency across the application.
 *
 * RPC calls are routed through the Supabase solana-rpc-proxy edge function
 * to avoid CORS issues. The proxy handles auth and rate limiting.
 *
 * Config resolution order:
 * 1. Supabase RPC proxy (preferred - avoids CORS)
 * 2. window.__SOLANA_PUBLIC_CONFIG__ (runtime-loaded)
 * 3. import.meta.env.VITE_* (build-time)
 * 4. Fallback to public Solana RPC (not recommended)
 */

import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { debug } from "@/lib/debug";

// Extend Window type to include RPC config
declare global {
  interface Window {
    __SOLANA_PUBLIC_CONFIG__?: {
      tritonRpcUrl?: string;
      tritonWsUrl?: string;
      alchemyRpcUrl?: string;
    };
  }
}

// Read environment variables (build-time)
export const USE_DEVNET = import.meta.env.VITE_USE_DEVNET === 'true';

/**
 * Get Triton RPC URL from runtime config or build-time env
 */
export function getTritonRpcUrl(): string {
  // Priority 1: Runtime-loaded config
  if (typeof window !== 'undefined' && window.__SOLANA_PUBLIC_CONFIG__?.tritonRpcUrl) {
    return window.__SOLANA_PUBLIC_CONFIG__.tritonRpcUrl;
  }
  // Priority 2: Build-time env var
  return import.meta.env.VITE_TRITON_RPC_URL || '';
}

/**
 * Get Triton WebSocket URL from runtime config or build-time env
 */
export function getTritonWsUrl(): string {
  // Priority 1: Runtime-loaded config
  if (typeof window !== 'undefined' && window.__SOLANA_PUBLIC_CONFIG__?.tritonWsUrl) {
    return window.__SOLANA_PUBLIC_CONFIG__.tritonWsUrl;
  }
  // Priority 2: Build-time env var
  return import.meta.env.VITE_TRITON_WS_URL || '';
}

/**
 * Get Alchemy RPC URL from runtime config or build-time env
 * Note: Alchemy does NOT support WebSockets for Solana (HTTP only)
 */
export function getAlchemyRpcUrl(): string {
  // Priority 1: Runtime-loaded config
  if (typeof window !== 'undefined' && window.__SOLANA_PUBLIC_CONFIG__?.alchemyRpcUrl) {
    return window.__SOLANA_PUBLIC_CONFIG__.alchemyRpcUrl;
  }
  // Priority 2: Build-time env var
  return import.meta.env.VITE_ALCHEMY_RPC_URL || '';
}

/**
 * Get the Solana network based on environment
 */
export function getSolanaNetwork(): WalletAdapterNetwork.Devnet | WalletAdapterNetwork.Mainnet {
  return USE_DEVNET ? WalletAdapterNetwork.Devnet : WalletAdapterNetwork.Mainnet;
}

/**
 * Get the RPC endpoint for Solana connections (HTTP)
 *
 * Routes through the Supabase solana-rpc-proxy edge function. The proxy
 * forwards requests to Triton (primary) with Alchemy fallback + circuit
 * breaker.
 */
export function getSolanaEndpoint(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (supabaseUrl) {
    return `${supabaseUrl}/functions/v1/solana-rpc-proxy`;
  }

  throw new Error('[Solana Config] VITE_SUPABASE_URL not configured; cannot resolve solana-rpc-proxy endpoint.');
}

/**
 * Get the WebSocket endpoint for Solana subscriptions.
 *
 * Triton Whirligig is the only Solana WS provider (Alchemy does not
 * support Solana WS).
 */
export function getSolanaWsEndpoint(): string | undefined {
  const tritonWsUrl = getTritonWsUrl();
  if (tritonWsUrl) {
    return tritonWsUrl;
  }

  console.warn('[Solana Config] No Triton WS URL configured, WS unavailable');
  return undefined;
}

/**
 * Get Solscan URL for a given address or transaction
 */
export function getSolscanUrl(addressOrTx: string, type: 'account' | 'address' | 'tx' | 'token' = 'address'): string {
  // Mainnet URLs don't need cluster param
  return `https://solscan.io/${type}/${addressOrTx}`;
}

/**
 * Shorten a Solana address for display
 */
export function shortenAddress(address: string, chars = 4): string {
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}

/**
 * Mask API keys in URLs for safe logging/display
 */
export function maskEndpoint(endpoint: string | undefined): string {
  if (!endpoint) return '(not configured)';
  // Remove any query params that might contain keys
  const urlWithoutParams = endpoint.split('?')[0];
  return urlWithoutParams;
}

/**
 * Get current Solana configuration (for debugging)
 * Note: This is evaluated lazily, not at module load time
 */
export function getSolanaConfig() {
  return {
    rpcUrl: getSolanaEndpoint(),
    wsEndpoint: getSolanaWsEndpoint(),
    network: getSolanaNetwork(),
    isDevnet: USE_DEVNET,
  } as const;
}

// Log config on first access (lazy evaluation)
let configLogged = false;
export function logSolanaConfig(): void {
  if (configLogged) return;
  configLogged = true;
  
  const config = getSolanaConfig();
  debug.trace('[Solana Config] HTTP:', maskEndpoint(config.rpcUrl));
  debug.trace('[Solana Config] WS:', maskEndpoint(config.wsEndpoint));
  debug.trace('[Solana Config] Network:', config.network);
}
